import { CELL_COUNT } from "./geometry";
import { isPlayer, type GameOutcome, type GameState, type Player } from "./model";
import {
  GameStateValidationError,
  validateGameState,
  validateLegacyGameState,
  validateVersion2GameState,
  validateVersion3GameState,
  type StateValidationResult,
} from "./validation";

export const GAME_SCHEMA_VERSION = 1 as const;

export type SavedCell = readonly Player[] | null;

export type SavedGameV1 = Readonly<{
  schemaVersion: typeof GAME_SCHEMA_VERSION;
  activePlayer: Player;
  outcome: GameOutcome | null;
  /** Null means removed; arrays preserve bottom-to-top stack order. */
  cells: readonly SavedCell[];
}>;

export type GameParseError = Readonly<{
  code: "malformed" | "unsupported-version" | "illegal-state";
  message: string;
  details?: readonly string[];
}>;

export type GameParseResult =
  Readonly<{ ok: true; state: GameState }> | Readonly<{ ok: false; error: GameParseError }>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactlyKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  const actual = Object.keys(record).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

function malformed(message: string): GameParseResult {
  return Object.freeze({
    ok: false,
    error: Object.freeze({ code: "malformed", message }),
  });
}

type StateValidator = (value: unknown) => StateValidationResult;

function serializeGameWith(state: GameState, validate: StateValidator): SavedGameV1 {
  const validation = validate(state);
  if (!validation.ok) throw new GameStateValidationError(validation.issues);

  const savedCells = validation.state.cells.map((cell): SavedCell =>
    cell.kind === "removed" ? null : Object.freeze([...cell.stack]),
  );
  const outcome =
    validation.state.outcome === null ? null : Object.freeze({ ...validation.state.outcome });
  return Object.freeze({
    schemaVersion: GAME_SCHEMA_VERSION,
    activePlayer: validation.state.activePlayer,
    outcome,
    cells: Object.freeze(savedCells),
  });
}

export function serializeGame(state: GameState): SavedGameV1 {
  return serializeGameWith(state, validateGameState);
}

export function serializeLegacyGame(state: GameState): SavedGameV1 {
  return serializeGameWith(state, validateLegacyGameState);
}

export function serializeVersion2Game(state: GameState): SavedGameV1 {
  return serializeGameWith(state, validateVersion2GameState);
}

export function serializeVersion3Game(state: GameState): SavedGameV1 {
  return serializeGameWith(state, validateVersion3GameState);
}

function parseGameWith(input: unknown, validate: StateValidator): GameParseResult {
  if (!isRecord(input)) return malformed("Saved game must be an object.");
  if (!("schemaVersion" in input)) return malformed("Saved game schema version is missing.");
  if (input["schemaVersion"] !== GAME_SCHEMA_VERSION) {
    return Object.freeze({
      ok: false,
      error: Object.freeze({
        code: "unsupported-version",
        message: "Saved game schema version is not supported.",
      }),
    });
  }
  if (!hasExactlyKeys(input, ["schemaVersion", "activePlayer", "outcome", "cells"])) {
    return malformed("Saved game contains missing or unknown properties.");
  }
  if (!isPlayer(input["activePlayer"]) || !Array.isArray(input["cells"])) {
    return malformed("Saved game player or cells are malformed.");
  }
  if (input["cells"].length !== CELL_COUNT) {
    return Object.freeze({
      ok: false,
      error: Object.freeze({
        code: "illegal-state",
        message: `Saved game must contain exactly ${CELL_COUNT} cells.`,
      }),
    });
  }

  const cells = [];
  for (const savedCell of input["cells"]) {
    if (savedCell === null) {
      cells.push({ kind: "removed" as const });
    } else if (Array.isArray(savedCell) && savedCell.every(isPlayer)) {
      cells.push({ kind: "field" as const, stack: [...savedCell] });
    } else {
      return malformed("Saved game contains an invalid cell.");
    }
  }

  let outcome: GameOutcome | null;
  if (input["outcome"] === null) {
    outcome = null;
  } else if (
    isRecord(input["outcome"]) &&
    input["outcome"]["kind"] === "passage" &&
    hasExactlyKeys(input["outcome"], ["kind", "winner"]) &&
    isPlayer(input["outcome"]["winner"])
  ) {
    outcome = { kind: "passage", winner: input["outcome"]["winner"] };
  } else if (
    isRecord(input["outcome"]) &&
    input["outcome"]["kind"] === "no-moves" &&
    hasExactlyKeys(input["outcome"], ["kind", "winner", "loser"]) &&
    isPlayer(input["outcome"]["winner"]) &&
    isPlayer(input["outcome"]["loser"])
  ) {
    outcome = {
      kind: "no-moves",
      winner: input["outcome"]["winner"],
      loser: input["outcome"]["loser"],
    };
  } else {
    return malformed("Saved game outcome is malformed.");
  }

  const validation = validate({ cells, activePlayer: input["activePlayer"], outcome });
  if (validation.ok) {
    return Object.freeze({
      ok: true,
      state: validation.state,
    });
  }
  return Object.freeze({
    ok: false,
    error: Object.freeze({
      code: "illegal-state",
      message: "Saved game violates Passo state invariants.",
      details: Object.freeze(validation.issues.map((entry) => `${entry.path}: ${entry.message}`)),
    }),
  });
}

export function parseGame(input: unknown): GameParseResult {
  return parseGameWith(input, validateGameState);
}

export function parseLegacyGame(input: unknown): GameParseResult {
  return parseGameWith(input, validateLegacyGameState);
}

export function parseVersion2Game(input: unknown): GameParseResult {
  return parseGameWith(input, validateVersion2GameState);
}

export function parseVersion3Game(input: unknown): GameParseResult {
  return parseGameWith(input, validateVersion3GameState);
}
